/**
 * The authenticated shell: SidebarNav on the left, TopBar above the content.
 *
 * The Administration section, the main nav, the TopBar search and the create
 * button are all gated by role. That gate is an **affordance**, not the security
 * boundary — the server scopes every query by the principal's organisation and
 * answers an out-of-role call with 403 whatever the portal chooses to render.
 * Hiding links people cannot use is a courtesy; the reason it is safe to rely on
 * is that it is not what makes it safe.
 *
 * The shape follows the Figma frames on `DeCA · Portal web`: four flat nav
 * entries with administration as **one** entry that opens a tabbed page, the
 * user card pinned to the sidebar foot, and a global search in the TopBar. The
 * bell Figma draws beside it is deliberately absent — nothing in the addendum,
 * the design spec or the contract produces a notification, and a dead bell on a
 * compliance tool invites a click that can only disappoint.
 *
 * **On a phone or a tablet in portrait (≤900 px) the sidebar is a drawer** behind
 * ☰ (#111, `docs/superpowers/specs/2026-09-26-deca-portal-on-phone-design.md`
 * §4.1). It is the same `<nav>`, never a second copy: a closed drawer is hidden
 * by `styles/responsive/shell.css` alone, so the links stay in the DOM for every
 * test and every width, and what the markup carries is only its state —
 * `data-open` for the stylesheet, `aria-expanded` on ☰, and `inert` on the page
 * behind it, which makes it modal without a hand-rolled focus trap. Theme and
 * language are hidden from the compact top bar rather than moved into the
 * drawer: Perfil has both, and the saved theme is applied in `main.tsx`.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import logoUrl from '../assets/asgtrans-logo.svg'
import { Icon, type IconName } from '../ui/Icon.tsx'
import { Button, IconButton } from '../ui/primitives.tsx'
import { useDismissOnOutsideClick } from '../ui/useDismiss.ts'
import { useMediaQuery } from '../ui/useMediaQuery.ts'
import { useAuth } from '../app/auth.tsx'
import { landingPathFor } from '../deca/navigation.ts'
import { abilitiesFor, initialsOf, roleKey } from '../deca/roles.ts'
import { LEGAL_LABEL_KEYS, legalLanguage, linkableLegalDocs } from '../legal/LegalLinks.tsx'
import { legalPageUrl } from '../legal/pages.ts'
import { applyTheme, readTheme, type ThemeChoice } from '../app/theme.ts'
import { SUPPORTED_LANGUAGES } from '../app/i18n.ts'
import { useLanguageChoice } from '../app/useLanguageChoice.ts'
import { OrganisationSelector } from './OrganisationSelector.tsx'

interface NavEntry {
  to: string
  labelKey: string
  icon: IconName
  exact?: boolean
  /** Lights up for any path under this prefix, not just `to` (the admin tabs). */
  prefix?: string
}

/**
 * Five entries, flat. `Crear DeCA` is not among them: Figma promotes it to the
 * TopBar, where it stays reachable from every screen rather than competing with
 * the destinations in the nav.
 */
const MAIN_NAV: NavEntry[] = [
  { to: '/', labelKey: 'nav.panel', icon: 'dashboard', exact: true },
  { to: '/decas', labelKey: 'nav.decas', icon: 'file' },
  // Its own entry, not a tab inside `/decas`: these are documents another party issued, with no
  // estado, no CSV and no lifecycle of ours, and mixing them into the document list is exactly
  // the confusion the surface exists to prevent. `/decas-externas` is a separate top-level
  // segment, so `/decas`'s fuzzy match above does not light up for it.
  { to: '/decas-externas', labelKey: 'nav.externalDecas', icon: 'externalLink' },
  // The counterparties the create forms suggest (#159). Before Importar, which stays the last of
  // these: the FAQ and the guide follow it for a user without Administración.
  { to: '/libreta', labelKey: 'nav.addressBook', icon: 'book' },
  { to: '/importar', labelKey: 'nav.import', icon: 'upload' },
]

/**
 * The entries a platform role gets, and they must match `DecaReadSurface`'s route set exactly: a
 * nav link to a screen the guard bounces is worse than no link at all. The documents, and the
 * address book of the company chosen in «Empresa» (#159); the detail screens are reached from the
 * list.
 */
const READ_ONLY_NAV: NavEntry[] = [
  { to: '/decas', labelKey: 'nav.decas', icon: 'file' },
  { to: '/libreta', labelKey: 'nav.addressBook', icon: 'book' },
]

function NavLink({ entry }: { entry: NavEntry }) {
  const { t } = useTranslation()
  const matchRoute = useMatchRoute()
  const location = useLocation()

  // `/decas/nueva` must not light up `/decas`, so the list matches exactly and
  // the detail route is handled by its own prefix check below.
  const active = entry.prefix
    ? location.pathname.startsWith(entry.prefix)
    : entry.exact
      ? Boolean(matchRoute({ to: entry.to }))
      : Boolean(matchRoute({ to: entry.to, fuzzy: true }))

  return (
    <Link to={entry.to} className="nav-item" data-active={active}>
      <Icon name={entry.icon} size={18} />
      {t(entry.labelKey)}
    </Link>
  )
}

/**
 * ASGTRANS's quick-start guide, which they asked to have in the menu on 2026-09-22: the invitation
 * letter attaches it, and this is how everyone who already had an account finds it — always the
 * current version. The portal serves it at a fixed address (`quickGuide` in `vite.config.ts`).
 *
 * A plain link in a new tab, not a route: the browser shows the PDF itself. And for every signed-in
 * role: a customer's first steps are the guide's subject, and ASGTRANS's own staff are who customers
 * ask about them.
 */
const QUICK_GUIDE_URL = '/guia-rapida-deca.pdf'

function QuickGuideLink() {
  const { t } = useTranslation()
  return (
    <a className="nav-item" href={QUICK_GUIDE_URL} target="_blank" rel="noopener" title={t('nav.quickGuideHint')}>
      <Icon name="info" size={18} />
      {t('nav.quickGuide')}
    </a>
  )
}

/**
 * ASGTRANS's answers to the questions customers ask about the DeCA, which they asked on 2026-09-29
 * to have in the menu between the guide and Administración. It works like the guide: a fixed
 * address, a tab of its own, and every signed-in role. No letter attaches it, so the file is
 * `web/public/preguntas-frecuentes-deca.pdf`, which the build copies to the root as it is.
 */
const FAQ_URL = '/preguntas-frecuentes-deca.pdf'

function FaqLink() {
  const { t } = useTranslation()
  return (
    <a className="nav-item" href={FAQ_URL} target="_blank" rel="noopener" title={t('nav.faqHint')}>
      <Icon name="help" size={18} />
      {t('nav.faq')}
    </a>
  )
}

/**
 * The compact tier's literal, as the stylesheet writes it (see the tier table in tokens.css), and
 * asked the phone way round: where `matchMedia` is missing the answer is "desktop".
 */
const COMPACT_QUERY = '(max-width: 900px)'

/**
 * The drawer's state, and what it does while open.
 *
 * Open means "opened at this address": `openAt` is the `href` it was opened at, so any navigation —
 * a link, the search, a search-parameter change, the browser's back button — closes it in that same
 * render, with nothing to keep in sync. The record is then cleared, so that coming back to the same
 * address (forward, the pager) does not reopen it. Leaving the compact tier (a rotation, a resize)
 * clears it too: the drawer would otherwise stay open as the desktop sidebar, with the page inert.
 */
function useDrawer() {
  const location = useLocation()
  const compact = useMediaQuery(COMPACT_QUERY)
  const [openAt, setOpenAt] = useState<string | null>(null)
  const open = openAt === location.href
  const id = useId()
  const menuRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setOpenAt(null)
  }, [location.href, compact])

  useEffect(() => {
    if (!open) return
    const menuButton = menuRef.current
    closeRef.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenAt(null)
    }
    document.addEventListener('keydown', onKeyDown)
    // The page must not scroll under the drawer. Restored here, in the cleanup, so that a shell
    // unmounted while the drawer is open (a tap on a link to another screen) cannot leave it locked.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      // Back to ☰, which opened it — unless the shell itself is going away.
      if (menuButton?.isConnected) menuButton.focus()
    }
  }, [open])

  return {
    id,
    open,
    menuRef,
    closeRef,
    show: () => setOpenAt(location.href),
    close: () => setOpenAt(null),
  }
}

export function AppShell({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const drawer = useDrawer()

  // Administration is one nav entry opening a tabbed page. Where it lands
  // depends on what the principal may see: ASGTRANS starts on the client
  // companies, an organisation admin on its own users.
  const adminEntry: NavEntry = {
    to: abilities.platformAdmin ? '/admin/empresas' : '/admin/usuarios',
    labelKey: 'nav.administration',
    icon: 'building',
    prefix: '/admin',
  }

  return (
    <div className="shell">
      <nav
        id={drawer.id}
        className="sidebar"
        data-open={drawer.open}
        aria-label={t('nav.sectionMain')}
        // A tap on any link in the drawer closes it. Navigating would close it anyway, but not a
        // tap on the page already open, nor the FAQ or the quick guide, which open in tabs of their own.
        onClick={(event) => {
          if ((event.target as Element).closest('a')) drawer.close()
        }}
      >
        <div className="sidebar__head">
          {/* A super-admin's landing is /admin/empresas, not /: the Panel is a
              screen `DecaSurface` redirects it out of, and the brand link must
              not send anyone somewhere the guard immediately undoes. */}
          <Link to={landingPathFor(principal)} className="sidebar__brand">
            <img src={logoUrl} alt={t('app.operator')} className="sidebar__logo" />
          </Link>
          {/* A plain button, because IconButton forwards no ref. Its name is not `common.close`:
              it is always in the DOM, and a dialog's ✕ already answers to that. */}
          <button
            ref={drawer.closeRef}
            type="button"
            className="btn btn--icon sidebar__close"
            aria-label={t('nav.closeMenu')}
            onClick={drawer.close}
          >
            <Icon name="x" size={20} />
          </button>
        </div>

        <div className="sidebar__nav">
          {(abilities.handlesDecas ? MAIN_NAV : abilities.readsDecas ? READ_ONLY_NAV : []).map(
            (entry) => (
              <NavLink key={entry.to} entry={entry} />
            ),
          )}
          {abilities.administer ? <NavLink entry={adminEntry} /> : null}
          <FaqLink />
          <QuickGuideLink />
        </div>

        <SidebarUser />
      </nav>

      {/* Only while open, so there is nothing to hide the rest of the time. `onClick`, not
          `onMouseDown`: React's iOS click shim applies to `onClick` alone. */}
      {drawer.open ? <div className="shell__scrim" aria-hidden="true" onClick={drawer.close} /> : null}

      <div className="main" inert={drawer.open}>
        <header className="topbar">
          <button
            ref={drawer.menuRef}
            type="button"
            className="btn btn--icon topbar__menu"
            aria-label={t('nav.openMenu')}
            aria-expanded={drawer.open}
            aria-controls={drawer.id}
            onClick={drawer.show}
          >
            <Icon name="menu" size={20} />
          </button>

          <div className="topbar__titles">
            {title ? <h1 className="topbar__title">{title}</h1> : null}
            {subtitle ? <div className="topbar__subtitle">{subtitle}</div> : null}
          </div>

          <GlobalSearch />

          <span className="spacer" />

          {actions ? <div className="topbar__actions">{actions}</div> : null}
          <CreateDecaAction />
          <OrganisationSelector />
          {/* Hidden in the compact top bar, not moved: Perfil carries both. */}
          <div className="topbar__prefs">
            <ThemeToggle />
            <LanguageToggle />
          </div>
          <UserMenu />
        </header>

        <main className="content">{children ?? <Outlet />}</main>
      </div>
    </div>
  )
}

/**
 * What the box in the top bar searches on a given screen: the list it writes `q` for, and the words
 * that say so.
 */
interface SearchTarget {
  to: '/decas' | '/admin/empresas' | '/admin/usuarios' | '/admin/auditoria'
  /** The grey text in the box: what can be searched here. */
  placeholderKey: string
  /** The box's accessible name. */
  labelKey: string
}

const DOCUMENTS: SearchTarget = {
  to: '/decas',
  placeholderKey: 'common.searchPlaceholder',
  labelKey: 'common.search',
}

/**
 * The «Administración» tabs that have a list to search. Each list's server search already existed
 * (companies by name or NIF, users by name or email, the trail by action); what they lacked, or
 * kept in their own card, was the box.
 */
const ADMIN_SEARCHES: Record<string, SearchTarget> = {
  '/admin/empresas': {
    to: '/admin/empresas',
    placeholderKey: 'admin.organisations.searchPlaceholder',
    labelKey: 'admin.organisations.searchLabel',
  },
  '/admin/usuarios': {
    to: '/admin/usuarios',
    placeholderKey: 'admin.users.searchPlaceholder',
    labelKey: 'admin.users.searchLabel',
  },
  '/admin/auditoria': {
    to: '/admin/auditoria',
    placeholderKey: 'admin.audit.searchPlaceholder',
    labelKey: 'admin.audit.searchLabel',
  },
}

/**
 * In «Administración» the box searches the tab that is open, and a tab with nothing to search
 * (keys, service accounts, the company's own settings) has no box. Everywhere else it searches
 * documents.
 */
function searchTargetFor(pathname: string): SearchTarget | null {
  if (pathname.startsWith('/admin/')) return ADMIN_SEARCHES[pathname] ?? null
  return DOCUMENTS
}

/**
 * The one search box in the portal. It writes a list's `q` parameter, so a search is a link a
 * colleague can be sent — which is the whole reason the filters live in the URL.
 *
 * **One box, and it searches the list in front of the user** (ASGTRANS, 2026-10-13). It used to
 * search documents on every screen: a company's name typed on «Empresas cliente» landed on the list
 * of documents, and a second box in that card (#203) put two on one screen.
 *
 * Outside «Administración» it is the documents' search, from any screen, and hidden from a role with
 * no document surface at all: it could only navigate to `/decas`, and the guard would bounce that
 * role back out. `readsDecas` rather than `handlesDecas`, because `/decas` is the platform roles'
 * screen too — searching by document number across every company is the support journey the
 * cross-company work exists for.
 */
function GlobalSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { principal } = useAuth()
  const target = searchTargetFor(location.pathname)
  /** On the list the box searches, as opposed to a screen it would leave for the documents. */
  const onList = target !== null && location.pathname === target.to
  // The search the list is filtered by, which is what the box has to show: it wrote those words
  // into the address, and until 2026-10-13 never read them back. Typed on any other screen, they
  // filtered the list and the box stood empty.
  const listSearch = (location.search as { q?: unknown }).q
  const q = onList && typeof listSearch === 'string' ? listSearch : ''
  const box = useRef<HTMLInputElement>(null)
  // On the list the address changes under a box that stays mounted: Back, forward, «Limpiar
  // filtros». Set, not remounted, so a search typed here keeps its cursor.
  useEffect(() => {
    if (box.current && box.current.value !== q) box.current.value = q
  }, [q])

  if (!target) return null
  // An «Administración» tab is its own guard: whoever sees the list may search it.
  if (target === DOCUMENTS && !abilitiesFor(principal).readsDecas) return null

  return (
    <form
      className="topbar__search"
      role="search"
      // On a phone the search keeps a row of its own on the list it searches, whose only free-text
      // search it is, and gives way everywhere else (#111).
      data-on-list={onList}
      onSubmit={(event) => {
        event.preventDefault()
        const value = new FormData(event.currentTarget).get('q')
        const q = typeof value === 'string' && value.trim() ? value.trim() : undefined
        // A new search starts on its first page.
        void navigate({ to: target.to, search: { q, page: 1 } })
      }}
    >
      <Icon name="search" size={16} />
      <input
        className="input"
        type="search"
        name="q"
        ref={box}
        // Remounts when the user leaves or reaches a list, another tab's included, so the box
        // reflects that list's filter instead of stale text.
        key={onList ? target.to : 'other'}
        defaultValue={q}
        placeholder={t(target.placeholderKey)}
        aria-label={t(target.labelKey)}
      />
    </form>
  )
}

/** Figma carries `+ Crear DeCA` on every TopBar; it is noise on the create screens themselves. */
function CreateDecaAction() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { principal } = useAuth()

  if (!abilitiesFor(principal).writeDecas) return null
  if (location.pathname.startsWith('/decas/nueva') || location.pathname.startsWith('/decas/agrupar')) {
    return null
  }

  // On a phone the label is hidden by the stylesheet and the button is its ＋ alone. The text stays
  // in the DOM, so it remains the button's name; an `aria-label` would have to repeat it.
  return (
    <Button
      variant="primary"
      icon="plus"
      className="topbar__create"
      onClick={() => void navigate({ to: '/decas/nueva' })}
    >
      <span className="topbar__create-label">{t('nav.create')}</span>
    </Button>
  )
}

/**
 * The user card at the foot of the sidebar. The card itself opens the profile;
 * sign-out is its own control beside it, because a button nested inside a link
 * is neither valid nor operable by keyboard.
 */
function SidebarUser() {
  const { t } = useTranslation()
  const { principal, signOut } = useAuth()
  const navigate = useNavigate()

  if (!principal) return null

  return (
    <div className="sidebar__user">
      <Link to="/perfil" className="sidebar__user-link">
        <span className="avatar" aria-hidden="true">
          {initialsOf(principal.fullName)}
        </span>
        <span className="sidebar__user-text">
          <span className="sidebar__user-name">{principal.fullName ?? principal.email}</span>
          <span className="sidebar__user-role">
            {principal.role ? t(roleKey(principal.role)) : t('common.user')}
          </span>
        </span>
      </Link>
      <IconButton
        icon="logout"
        label={t('common.logout')}
        onClick={() => void signOut().then(() => navigate({ to: '/login' }))}
      />
    </div>
  )
}

function ThemeToggle() {
  const { t } = useTranslation()
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme())

  useEffect(() => applyTheme(choice), [choice])

  const next: ThemeChoice = choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system'
  const labels: Record<ThemeChoice, string> = {
    system: t('common.themeSystem'),
    light: t('common.themeLight'),
    dark: t('common.themeDark'),
  }

  return (
    <IconButton
      icon={choice === 'dark' ? 'moon' : 'sun'}
      label={`${t('common.theme')}: ${labels[choice]}`}
      onClick={() => setChoice(next)}
    />
  )
}

function LanguageToggle() {
  const { t } = useTranslation()
  // Same hook the Profile screen's picker uses (`app/useLanguageChoice.ts`) — this toggle
  // used to keep its own copy of "switch and remember", which is exactly the two thirds of
  // the behaviour that never persisted: it changed `i18n` and `localStorage` but never wrote
  // to the server, so it silently reverted on the next silent token refresh.
  const { language: current, choose } = useLanguageChoice()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismissOnOutsideClick(ref, () => setOpen(false))

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="globe" size={18} />
        <span className="small" style={{ textTransform: 'uppercase', fontWeight: 700 }}>
          {current}
        </span>
      </button>
      {open ? (
        <div className="menu" role="menu">
          <span className="menu__label">{t('common.language')}</span>
          {SUPPORTED_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => {
                void choose(language)
                setOpen(false)
              }}
            >
              {language === current ? <Icon name="check" size={16} /> : <span style={{ width: 16 }} />}
              {language === 'es' ? 'Español' : 'English'}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The TopBar avatar. Name and role moved to the sidebar card, so this is the
 * avatar alone — a second place to read the same two lines would be the kind of
 * duplication the flat nav was meant to remove.
 */
function UserMenu() {
  const { t, i18n } = useTranslation()
  const { principal, signOut } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismissOnOutsideClick(ref, () => setOpen(false))

  return (
    <div className="user-menu topbar__account" ref={ref}>
      <button
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={principal?.fullName ?? principal?.email ?? t('common.user')}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar" aria-hidden="true">
          {initialsOf(principal?.fullName)}
        </span>
      </button>
      {open ? (
        <div className="menu" role="menu">
          <span className="menu__label">
            {principal?.role ? t(roleKey(principal.role)) : t('common.user')}
          </span>
          <Link to="/perfil" role="menuitem" className="menu__item" onClick={() => setOpen(false)}>
            <Icon name="user" size={16} />
            {t('nav.profile')}
          </Link>
          {linkableLegalDocs().map((key) => (
            <a
              key={key}
              href={legalPageUrl(key, legalLanguage(i18n.language))}
              target="_blank"
              rel="noopener"
              role="menuitem"
              className="menu__item"
              onClick={() => setOpen(false)}
            >
              <Icon name={key === 'privacidad' ? 'file' : 'info'} size={16} />
              {t(LEGAL_LABEL_KEYS[key])}
            </a>
          ))}
          <div className="menu__separator" />
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setOpen(false)
              void signOut().then(() => navigate({ to: '/login' }))
            }}
          >
            <Icon name="logout" size={16} />
            {t('common.logout')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
